// Лёгкая сессия владельца ресторана в localStorage.
// refresh_token живёт долго (30 дней), access_token, 15 минут; api.ts умеет
// прозрачно обновлять access при 401.

import { useSyncExternalStore } from 'react';

const ACCESS_KEY = 'resstro.owner.access';
const REFRESH_KEY = 'resstro.owner.refresh';
const USER_KEY = 'resstro.owner.user';

export interface OwnerUser {
  id: string;
  email: string | null;
  name: string | null;
  phone: string | null;
  role: 'OWNER';
  establishmentId: string | null;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  user: OwnerUser;
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function getUser(): OwnerUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as OwnerUser) : null;
  } catch {
    return null;
  }
}

export function saveSession(res: LoginResponse): void {
  localStorage.setItem(ACCESS_KEY, res.access_token);
  localStorage.setItem(REFRESH_KEY, res.refresh_token);
  localStorage.setItem(USER_KEY, JSON.stringify(res.user));
  notify();
}

export function updateAccessToken(token: string, refreshToken?: string): void {
  localStorage.setItem(ACCESS_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  notify();
}

export function updateUser(u: OwnerUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(u));
  notify();
}

export function logout(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
  notify();
}

export function isLoggedIn(): boolean {
  return !!getAccessToken();
}

// Подписка на изменения (Header и Cabinet реагируют на login/logout).
type Listener = () => void;
const listeners = new Set<Listener>();

export function onAuthChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify() {
  for (const l of listeners) l();
}

const serverSnapshot = () => false;

// Реактивный флаг «залогинен» для компонентов (Header, Hero, Pricing, CTA).
// Страницы пререндерятся без доступа к localStorage, поэтому на сервере и при
// гидрации всегда false, разметка совпадает с HTML, а React сразу после
// гидрации перерисует с настоящим значением без ошибок mismatch.
export function useIsLoggedIn(): boolean {
  return useSyncExternalStore(onAuthChange, isLoggedIn, serverSnapshot);
}

// Смена в других вкладках, синхронизируем.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === ACCESS_KEY || e.key === USER_KEY) notify();
  });
}
